import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Filters } from '../../components/filters';
import { userSearchDebounce } from '../../components/hooks/use-search';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { PageHeader } from '../../components/page-header';
import { CellProps, TableComponent } from '../../components/table';
import { Api } from '../../core/api';
import { FacilitySearchSimpleDto } from '../../core/api/StudioClient';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';

function getStatusBadgeVariant(
    status: FacilitySearchSimpleDto['status']
): 'success' | 'destructive' | 'muted' {
    switch (status) {
        case 'DELETED':
            return 'destructive';
        case 'VERIFIED':
            return 'success';
        default:
        case 'NEW':
            return 'muted';
    }
}

type StatusFiltersType = FacilitySearchSimpleDto['status'] | 'ALL';
type FacilityFiltersSource = FacilitySearchSimpleDto['source'] | 'ALL';

export const FacilityList = observer(() => {
    const navigate = useNavigate();
    const { studioClient } = injectTSDI(Api);
    const [statusFilter, setStatusFilter] =
        React.useState<StatusFiltersType>('NEW');
    const [facilitySourceFilter, setFacilitySourceFilter] =
        React.useState<FacilityFiltersSource>('ALL');
    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    const { setSearchTerm } = userSearchDebounce(dispatch);

    const facilities = useWrapRequest(
        async (searchParams, statusFilter, facilitySourceFilter) => {
            const result =
                await studioClient.v1.closedFacilityControllerGetPage({
                    ...searchParams,
                    ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
                    ...(facilitySourceFilter !== 'ALL'
                        ? { source: facilitySourceFilter }
                        : {})
                });

            return { ...result.data, content: result.data.content || [] };
        },
        { defaultData: { content: [] } }
    );

    const createFacilityHandler = React.useCallback(() => {
        navigate(links.facilityCreate());
    }, [navigate]);

    React.useEffect(() => {
        facilities.request([searchParams, statusFilter, facilitySourceFilter]);
    }, [searchParams]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [statusFilter]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [facilitySourceFilter]);

    return (
        <>
            <PageHeader
                title="Facilities"
                actions={
                    <Button onClick={createFacilityHandler}>
                        <Plus />
                        Create facility
                    </Button>
                }
            />
            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<StatusFiltersType, false>
                    label="Status"
                    value={statusFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        { label: 'new', value: 'NEW' },
                        { label: 'verified', value: 'VERIFIED' },
                        { label: 'deleted', value: 'DELETED' }
                    ]}
                    onChange={(val) => void setStatusFilter(val)}
                />
                <Filters<FacilityFiltersSource, false>
                    label="Source"
                    value={facilitySourceFilter}
                    width={180}
                    options={[
                        { label: 'All', value: 'ALL' },
                        { label: 'OpenStreetMap', value: 'OSM' },
                        { label: 'Salesforce', value: 'SALESFORCE' },
                        { label: 'MySports', value: 'MYSPORTS' },
                        { label: 'Move Republic', value: 'MOVE_REPUBLIC' },
                        { label: 'Magicline', value: 'MAGICLINE' },
                        { label: 'Google', value: 'GOOGLE_PLACES' },
                        { label: 'Admin Portal', value: 'ADMIN_PORTAL' }
                    ]}
                    onChange={(val) => {
                        setFacilitySourceFilter(val);
                    }}
                />
            </div>
            <TableComponent<FacilitySearchSimpleDto[]>
                emptyText="No entries"
                items={facilities}
                columns={[
                    {
                        Header: 'Studio Id',
                        accessor: (value) => value.id,
                        style: { width: 110 }
                    },
                    {
                        Header: 'Name',
                        Cell: ({
                            row: {
                                original: { address, name }
                            }
                        }: CellProps<FacilitySearchSimpleDto>) => (
                            <div className="flex min-w-0 flex-col">
                                <span className="truncate text-sm font-medium">
                                    {name}
                                </span>
                                <span className="truncate text-xs text-muted-foreground">
                                    {address.city}
                                    {address.street && `, ${address.street}`}
                                    {address.houseNumber &&
                                        ` ${address.houseNumber}, `}
                                    {address.zip && `${address.zip}, `}
                                    {address.countryWithTranslation &&
                                        `${address.countryWithTranslation.translation}`}
                                </span>
                            </div>
                        ),
                        style: { width: 420 }
                    },
                    {
                        Header: 'Category',
                        accessor: (value) => value.category.label,
                        style: { width: 150 }
                    },
                    {
                        Header: 'Status',
                        Cell: ({
                            row: {
                                original: { status }
                            }
                        }: CellProps<FacilitySearchSimpleDto>) => (
                            <Badge variant={getStatusBadgeVariant(status)}>
                                {status}
                            </Badge>
                        ),
                        style: { width: 110 }
                    },
                    {
                        Header: 'Source',
                        accessor: (value) => value.source,
                        style: { width: 150 }
                    },
                    {
                        Header: '',
                        accessor: 'id',
                        Cell: ({
                            row: {
                                original: { id, status }
                            }
                        }: CellProps<FacilitySearchSimpleDto>) => (
                            <Link
                                to={links.facilityEdit(id)}
                                className="text-primary hover:underline"
                            >
                                {status === 'NEW' ? 'Verify' : 'Edit'}
                            </Link>
                        ),
                        style: { textAlign: 'right', width: 90 }
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                searchPlaceholder="Search by name, address or paste the whole ID..."
                onSearchChange={(val) => void setSearchTerm(val)}
            />
        </>
    );
});

export default FacilityList;
